import { ExplorerDataNode } from "../../file-explorer/treeDataManipulation";
import { FieldDataNode } from "rc-tree";
import { RegisterOptions, useForm } from "react-hook-form";
import { Button, Dialog, LabeledInput } from "@itwin/itwinui-react";
import { createNewFile } from "../actions/createNewFile";

interface CreateNewFileDialogOptions {
  isOpen: boolean;
  onClose: (filePath?: string) => void;
  targetNode: FieldDataNode<ExplorerDataNode>;
  isFolder: boolean;
}

type FormData = {
  name: string;
};

const defaultValues: FormData = {
  name: ""
}

export const NewFileDialog = ({ isOpen, onClose, targetNode, isFolder }: CreateNewFileDialogOptions) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({ defaultValues });

  const fileNameValidationRules: RegisterOptions = {
    required: {
      value: true,
      message: "Name entered cannot be empty"
    },
    maxLength: {
      value: 128,
      message: "Name entered is too long"
    },
    pattern: {
      value: isFolder ? /^[\w-]*$/ : /^[\w-\.]*$/,
      message: "Only alphanumeric characters are allowed"
    },
    validate: (value) => {
      if (targetNode) {
        const siblings = targetNode.isLeaf ? targetNode.parentNode?.children : targetNode.children;
        const match = siblings?.find((node) => node.title?.toString().toLowerCase() === value.toLowerCase());
        if (match)
          return match.isLeaf ? "There is a file with the same name already" : "There is a folder with the same name already";
      }
    }
  };

  const onCancel = () => {
    // need to defer a form reset a little, because there is a fade-out of modal dialog
    // otherwise it's noticible that form is wiped out immediatelly after submit
    setTimeout(() => reset(defaultValues), 250);
    onClose();
  }

  const onAccept = (name: string) => {
    setTimeout(() => reset(defaultValues), 250);
    const newFilePath = createNewFile(name, targetNode)
    onClose(newFilePath);
  }

  return (
    <Dialog isOpen={isOpen} onClose={onCancel} closeOnEsc closeOnExternalClick preventDocumentScroll trapFocus setFocus isDismissible >
      <Dialog.Backdrop />
      <Dialog.Main>
        <Dialog.TitleBar titleText={isFolder ? "New Folder" : "New File"} />
        <form onSubmit={handleSubmit(({ name }) => onAccept(name))} noValidate autoComplete="off">
          <Dialog.Content style={{ height: "64px" }}>
            <LabeledInput
              placeholder="Enter new name..."
              message={errors.name?.message}
              status={errors.name ? "warning" : undefined}
              {...register("name", fileNameValidationRules)}
            />
          </Dialog.Content>
          <Dialog.ButtonBar>
            <Button styleType="high-visibility" type="submit" disabled={Boolean(errors.name)}>OK</Button>
            <Button onClick={onCancel}>Cancel</Button>
          </Dialog.ButtonBar>
        </form>
      </Dialog.Main>
    </Dialog>
  )
}
